import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTheme } from 'next-themes';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { LoginPage } from '@/components/auth/LoginPage';
import { IntercomFlow } from '@/components/intercom/IntercomFlow';
import {
  DynamicBackground,
  type BackgroundConfig,
} from '@/components/layout/DynamicBackground';
import { resolveLandingText, type LandingConfig } from '@/lib/landingConfig';

interface OrgContext {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  primary_color?: string;
  logo_light_url?: string;
  logo_dark_url?: string;
  auth_config?: {
    bankid_enabled?: boolean;
    invite_enabled?: boolean;
    allowed_methods?: string[];
    primary_method?: string;
    method_configs?: Record<string, { enabled?: boolean; [key: string]: any }>;
    access?: {
      allowed_methods?: string[];
      primary_method?: string;
    };
  };
  background_config?: BackgroundConfig | null;
  landing_config?: LandingConfig | null;
}

interface OrgSettings {
  logo_light_url?: string;
  logo_dark_url?: string;
  primary_color?: string;
  default_theme?: string;
  shop_banner_gradient_from?: string;
  shop_banner_gradient_via?: string;
  shop_banner_gradient_to?: string;
}

const RESERVED_PATHS = ['auth', 'keys', 'devices', 'device', 'history', 'admin', 'unlock', 'intercom', 'bankid-callback', 'send-key', 'profile', 'guest-key'];

export default function OrgLandingPage() {
  const { orgSlug = '', deviceCode: deviceCodeParam } = useParams<{ orgSlug: string; deviceCode?: string }>();
  const [searchParams] = useSearchParams();
  // Device code from path (/:orgSlug/:deviceCode) takes priority over query param (?d=)
  const deviceCode = deviceCodeParam || searchParams.get('d') || '';
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { resolvedTheme } = useTheme();

  const [orgContext, setOrgContext] = useState<OrgContext | null>(null);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);
  const [deviceAuthConfig, setDeviceAuthConfig] = useState<OrgContext['auth_config'] | null>(null);
  const [deviceLandingConfig, setDeviceLandingConfig] = useState<LandingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showIntercom, setShowIntercom] = useState(false);
  const [hasIntercomResidents, setHasIntercomResidents] = useState(false);

  // Fetch organization by slug + org settings for branding
  // When deviceCode is present, also fetch device-level auth overrides.
  useEffect(() => {
    if (RESERVED_PATHS.includes(orgSlug)) return;

    const fetchOrg = async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, slug, logo_url, primary_color, logo_light_url, logo_dark_url, auth_config, background_config, landing_config')
        .eq('slug', orgSlug)
        .single();

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setOrgContext(data as OrgContext);

      // Persist slug for post-logout redirect (include device code if present)
      const slugPath = deviceCode ? `/${orgSlug}/${deviceCode}` : `/${orgSlug}`;
      sessionStorage.setItem('last_slug_path', slugPath);
      sessionStorage.setItem('org_context', JSON.stringify({ org_slug: orgSlug, org_id: data.id, org_name: data.name }));

      // Fetch device-specific auth config when device code is present
      if (deviceCode) {
        const { data: deviceCtx } = await supabase.rpc('get_device_landing_context', {
          p_device_code: deviceCode,
        });
        if (deviceCtx?.auth_config) {
          setDeviceAuthConfig(deviceCtx.auth_config as OrgContext['auth_config']);
        }
        // Device-level landing text wins over org defaults (device inherits any
        // field it doesn't override — the RPC already COALESCEs device→org).
        if (deviceCtx?.landing_config) {
          setDeviceLandingConfig(deviceCtx.landing_config as LandingConfig);
        }
      }

      // Fetch organization_settings for richer branding
      const { data: settings } = await supabase
        .from('organization_settings')
        .select('logo_light_url, logo_dark_url, primary_color, default_theme, shop_banner_gradient_from, shop_banner_gradient_via, shop_banner_gradient_to')
        .eq('organization_id', data.id)
        .maybeSingle();

      if (settings) {
        setOrgSettings(settings as OrgSettings);
      }

      setLoading(false);
    };

    fetchOrg();
  }, [orgSlug, deviceCode]);

  // Check if device has intercom residents (only if device_code is provided)
  useEffect(() => {
    if (!deviceCode || !orgContext?.id) return;

    async function checkIntercom() {
      const { intercomAction } = await import('@/lib/api');
      const result = await intercomAction<{ residents: any[] }>('intercom-residents', {
        device_code: deviceCode,
        organization_id: orgContext!.id,
      });
      if (result.data && result.data.residents.length > 0) {
        setHasIntercomResidents(true);
      }
    }

    checkIntercom();
  }, [deviceCode, orgContext?.id]);

  // Detect open access mode: all methods explicitly disabled via method_configs.
  // Uses device-level auth_config when available, otherwise org-level.
  const isOpenAccess = (() => {
    const ac = deviceAuthConfig ?? orgContext?.auth_config;
    const mc = ac?.method_configs;
    if (!mc) return false;
    const methods = ['email', 'phone', 'bankid', 'invite'] as const;
    return methods.every(m => mc[m]?.enabled === false);
  })();

  // Auto-redirect authenticated users, or open access mode (no auth required)
  useEffect(() => {
    if (!authLoading && orgContext) {
      if (user || isOpenAccess) {
        navigate('/keys');
      }
    }
  }, [user, authLoading, orgContext, isOpenAccess, navigate]);

  if (RESERVED_PATHS.includes(orgSlug)) return null;

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-6">
        <h1 className="text-2xl font-bold mb-2">Organisationen hittades inte</h1>
        <p className="text-muted-foreground">
          Kontrollera att länken är korrekt
        </p>
      </main>
    );
  }

  // Resolve logo: settings > org table, theme-aware
  const logoLightUrl = orgSettings?.logo_light_url || orgContext?.logo_light_url;
  const logoDarkUrl = orgSettings?.logo_dark_url || orgContext?.logo_dark_url;
  const orgLogoUrl = resolvedTheme === 'dark'
    ? (logoDarkUrl || logoLightUrl || orgContext?.logo_url)
    : (logoLightUrl || logoDarkUrl || orgContext?.logo_url);

  // Derive allowed login methods. When a device code is present and has
  // its own auth_config, that takes priority over the org-level config.
  // Source-of-truth precedence:
  //   0. Device-level auth_config (when deviceCode present + device has overrides)
  //   1. `auth_config.method_configs` (rich per-method config from CMS)
  //   2. `auth_config.access.allowed_methods` (nested legacy shape)
  //   3. `auth_config.allowed_methods` (flat shape saved by OrgBrandingConfig)
  //   4. Legacy `bankid_enabled=false` toggle
  const effectiveAuthConfig = deviceAuthConfig ?? orgContext?.auth_config;
  const methodConfigs = effectiveAuthConfig?.method_configs;
  const accessConfig = effectiveAuthConfig?.access;

  const ACCESS_METHODS = ['email', 'phone', 'bankid', 'invite'] as const;
  const effectiveMethods = methodConfigs
    ? ACCESS_METHODS.filter(m => methodConfigs[m]?.enabled !== false)
    : accessConfig?.allowed_methods
      ?? effectiveAuthConfig?.allowed_methods
      ?? (effectiveAuthConfig?.bankid_enabled === false
        ? ACCESS_METHODS.filter(m => m !== 'bankid')
        : undefined);

  const primaryMethod = accessConfig?.primary_method
    ?? effectiveAuthConfig?.primary_method;

  const backgroundConfig = orgContext?.background_config ?? null;
  const primaryColor = orgContext?.primary_color;

  // Landing text/visibility: device override (when present) wins over org default.
  const landingText = resolveLandingText(
    deviceLandingConfig ?? orgContext?.landing_config,
  );

  // Solid base layer sits above the global AuroraBackground (-z-10) but below
  // the org-configured background, guaranteeing a clean canvas for the org design.
  const baseLayer = backgroundConfig ? (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none bg-background"
      style={{ zIndex: -1 }}
    />
  ) : null;

  if (showIntercom && deviceCode && orgContext) {
    return (
      <>
        {baseLayer}
        <DynamicBackground config={backgroundConfig} />
        <IntercomFlow
          deviceCode={deviceCode}
          organizationId={orgContext.id}
          orgName={orgContext.name}
          orgLogoUrl={orgLogoUrl}
          onBack={() => setShowIntercom(false)}
        />
      </>
    );
  }

  return (
    <>
      {baseLayer}
      <DynamicBackground config={backgroundConfig} />
      <LoginPage
        orgName={orgContext?.name}
        orgSlug={orgSlug}
        orgLogoUrl={orgLogoUrl}
        allowedMethods={effectiveMethods}
        primaryMethod={primaryMethod}
        primaryColor={primaryColor}
        hasCustomBackground={!!backgroundConfig}
        headline={landingText.headline}
        subheadline={landingText.subheadline}
        showLogo={landingText.showLogo}
        showHeadline={landingText.showHeadline}
        showSubheadline={landingText.showSubheadline}
        onIntercomClick={hasIntercomResidents ? () => setShowIntercom(true) : undefined}
      />
    </>
  );
}
